import { useEffect, useRef, useState, useCallback } from 'react'
import { useOnline } from './useOnline'
import { getPendingDrafts, markDraftSynced, getPendingDeliveryDrafts, markDeliveryDraftSynced, deleteDeliveryDraft } from '@/lib/db'
import { pickupRecordsApi, deliveriesApi } from '@/lib/api'
import { getDraftStartedAt } from '@/lib/pickupDraftHelpers'
import { parseSyncError } from '@/lib/syncErrors'
import { reportSyncError } from '@/lib/reportError'
import type { DeliveryDraft, PickupRecordDraft } from '@/types'

export interface SyncErrorEntry {
  draftId?: number
  kind: 'pickup' | 'delivery'
  /** Etiqueta del registro afectado, p.ej. "Retiro #5" o "Entrega #3". */
  recordLabel: string
  /** Mensaje apto para mostrar al usuario final. */
  message: string
}

export interface SyncState {
  isSyncing: boolean
  lastSyncAt: Date | null
  pendingCount: number
  syncErrors: SyncErrorEntry[]
}

export function useSync() {
  const isOnline = useOnline()
  const [syncState, setSyncState] = useState<SyncState>({
    isSyncing: false,
    lastSyncAt: null,
    pendingCount: 0,
    syncErrors: [] as SyncErrorEntry[],
  })
  const isSyncingRef = useRef(false)

  const refreshPendingCount = useCallback(async () => {
    try {
      const [drafts, deliveryDrafts] = await Promise.all([
        getPendingDrafts(),
        getPendingDeliveryDrafts(),
      ])
      setSyncState((prev) => ({ ...prev, pendingCount: drafts.length + deliveryDrafts.length }))
    } catch {
      // silencioso
    }
  }, [])

  const syncDraft = async (draft: PickupRecordDraft): Promise<void> => {
    if (!draft.id) return

    const invalidItem = draft.items.find(
      (item) => item.kilos > 0 && item.segregation_status === ''
    )

    if (invalidItem) {
      throw new Error('Falta indicar la calidad de segregación en uno o más residuos de este registro.')
    }

    if (!draft.finished_at) {
      throw new Error('Este registro no tiene hora de término registrada y no puede enviarse.')
    }

    const payload = {
      pickup_id: draft.pickup_id,
      local_uuid: draft.local_uuid,
      started_at: getDraftStartedAt(draft),
      finished_at: draft.finished_at,
      notes: draft.notes,
      items: draft.items.map((item) => ({
        waste_type_id: item.waste_type_id,
        kilos: item.kilos,
        segregation_status: item.segregation_status || 'good',
        notes: item.notes,
      })),
    }

    // 1. Crear o actualizar el registro en el servidor.
    //    Si el draft ya fue sincronizado antes (server_id presente), actualizamos
    //    para evitar duplicados y doble subida de fotos en caso de race con handleSave.
    const recordRes = draft.server_id
      ? await pickupRecordsApi.update(draft.server_id, payload)
      : await pickupRecordsApi.create(payload)

    const serverId = recordRes.data.id
    const serverItems = recordRes.data.items ?? []

    // 2. Subir fotos si hay, asociando cada una al ítem correcto por waste_type_id.
    //    Resolver por waste_type_id en lugar de por índice de posición evita
    //    asociaciones incorrectas si los ítems se devuelven en distinto orden.
    const serverItemByWasteTypeId = Object.fromEntries(
      serverItems.map((si) => [si.waste_type_id, si])
    )

    const photoUploads = draft.items.flatMap((draftItem) => {
      const serverItemId = serverItemByWasteTypeId[draftItem.waste_type_id]?.id ?? null

      return (draftItem.photos ?? []).map((photo) =>
        pickupRecordsApi.uploadPhoto(serverId, photo, serverItemId).catch((err) => {
          console.error('Error subiendo foto:', err)
        })
      )
    })

    if (photoUploads.length > 0) {
      await Promise.allSettled(photoUploads)
    }

    // 3. Marcar como sincronizado en Dexie
    await markDraftSynced(draft.id, serverId)
  }

  const syncDeliveryDraft = async (draft: DeliveryDraft): Promise<void> => {
    if (!draft.id) return

    const document = draft.document
      ? new File([draft.document], draft.documentName ?? 'document', { type: draft.document.type })
      : null

    const res = await deliveriesApi.create(
      {
        destination_id: draft.destination_id,
        delivered_at: draft.delivered_at,
        notes: draft.notes || undefined,
        items: draft.items,
      },
      document,
    )

    await markDeliveryDraftSynced(draft.id, res.data.id)
  }

  const sync = useCallback(async () => {
    if (isSyncingRef.current) return

    const [drafts, deliveryDrafts] = await Promise.all([
      getPendingDrafts(),
      getPendingDeliveryDrafts(),
    ])

    if (drafts.length === 0 && deliveryDrafts.length === 0) {
      setSyncState((prev) => ({ ...prev, pendingCount: 0 }))
      return
    }

    isSyncingRef.current = true
    setSyncState((prev) => ({
      ...prev,
      isSyncing: true,
      syncErrors: [] as SyncErrorEntry[],
      pendingCount: drafts.length + deliveryDrafts.length,
    }))

    const errors: SyncErrorEntry[] = []

    for (const draft of drafts) {
      try {
        await syncDraft(draft)
      } catch (err) {
        const parsed = parseSyncError(err)
        const entry: SyncErrorEntry = {
          draftId: draft.id,
          kind: 'pickup',
          recordLabel: `Retiro #${draft.pickup_id}`,
          message: parsed.userMessage,
        }
        errors.push(entry)
        reportSyncError({ kind: 'pickup', draftId: draft.id, parsed })
      }
    }

    for (const draft of deliveryDrafts) {
      try {
        await syncDeliveryDraft(draft)
      } catch (err) {
        const parsed = parseSyncError(err)

        // Un 422 con errores de pickup_record_item_id indica que los ítems fueron
        // eliminados del servidor. Este draft nunca podrá sincronizar: se descarta
        // automáticamente para no bloquear al operador.
        const hasDeletedItems =
          parsed.fieldErrors &&
          Object.keys(parsed.fieldErrors).some((key) => key.includes('pickup_record_item_id'))

        if (hasDeletedItems && draft.id) {
          // El draft nunca podrá sincronizar: se descarta silenciosamente.
          // El evento queda registrado en Sentry para trazabilidad del equipo.
          await deleteDeliveryDraft(draft.id)
        } else {
          errors.push({
            draftId: draft.id,
            kind: 'delivery',
            recordLabel: 'Entrega pendiente',
            message: parsed.userMessage,
          })
        }

        reportSyncError({ kind: 'delivery', draftId: draft.id, parsed, draftItems: draft.items })
      }
    }

    const [remainingDrafts, remainingDeliveryDrafts] = await Promise.all([
      getPendingDrafts(),
      getPendingDeliveryDrafts(),
    ])

    setSyncState({
      isSyncing: false,
      lastSyncAt: new Date(),
      pendingCount: remainingDrafts.length + remainingDeliveryDrafts.length,
      syncErrors: errors,
    })
    isSyncingRef.current = false
  }, [])

  // Sincroniza automáticamente al recuperar conexión
  useEffect(() => {
    if (isOnline) {
      sync()
    }
  }, [isOnline, sync])

  // Actualiza el conteo de pendientes al montar
  useEffect(() => {
    refreshPendingCount()
  }, [refreshPendingCount])

  return {
    ...syncState,
    isOnline,
    sync,
    refreshPendingCount,
  }
}
